import { type CalendarEvent, type KioskSettings } from '@/types/calendar';

const STORAGE_KEY = 'kiosk-settings';

export function getSettings(): KioskSettings {
  const defaults: KioskSettings = {
    roomName: '',
    refreshInterval: 60,
    googleCalendarEnabled: false,
    googleResourceEmail: '',
    zoomEnabled: false,
    zoomApiKey: '',
    zoomApiSecret: '',
    zoomUserId: '',
    teamsEnabled: false,
    teamsClientId: '',
    teamsClientSecret: '',
    teamsTenantId: '',
    teamsUserId: '',
  };

  if (typeof window === 'undefined') {
    return defaults;
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return { ...defaults, ...JSON.parse(stored) };
    } catch {
      return defaults;
    }
  }

  return defaults;
}

export function saveSettings(settings: KioskSettings): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }
}

async function getGoogleApiKey(): Promise<string> {
  const response = await fetch('/api/config');
  if (!response.ok) {
    throw new Error('Failed to fetch API configuration');
  }
  const data = await response.json();
  return data.googleApiKey;
}

export async function fetchGoogleCalendarEvents(resourceEmail: string): Promise<CalendarEvent[]> {
  const apiKey = await getGoogleApiKey();

  if (!apiKey) {
    throw new Error(
      'Google API key is not configured. Please set GOOGLE_API_KEY in environment variables.'
    );
  }

  // Use current time in user's local timezone
  const now = new Date();

  // Get start of tomorrow in user's local timezone
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const timeMin = now.toISOString();
  const timeMax = tomorrow.toISOString();

  const url = new URL(
    `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(resourceEmail)}/events`
  );

  url.searchParams.append('key', apiKey);
  url.searchParams.append('timeMin', timeMin);
  url.searchParams.append('timeMax', timeMax);
  url.searchParams.append('singleEvents', 'true');
  url.searchParams.append('orderBy', 'startTime');
  url.searchParams.append('maxResults', '50');
  url.searchParams.append('conferenceDataVersion', '1');

  console.log('Fetching Google Calendar events:', {
    resourceEmail,
    url: url.toString().replace(apiKey, 'API_KEY_HIDDEN'),
    timeMin,
    timeMax,
  });

  const response = await fetch(url.toString());

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle Google API errors with more helpful messages
    if (errorData.error) {
      const { code, message } = errorData.error;

      if (code === 403 && message.includes('API has not been used')) {
        throw new Error(
          'Google Calendar API is not enabled. Please enable it in Google Cloud Console.'
        );
      }

      if (code === 403) {
        throw new Error('Access denied. Please check your API key and calendar permissions.');
      }

      if (code === 404) {
        throw new Error(
          'Calendar not found. Please ensure: 1) Calendar ID is correct, 2) Calendar is set to "Make available to public" in Google Calendar settings, or 3) Use OAuth instead of API key for private calendars.'
        );
      }

      throw new Error(message || `Failed to fetch calendar events: ${response.statusText}`);
    }

    throw new Error(`Failed to fetch calendar events: ${response.statusText}`);
  }

  const data = await response.json();
  return data.items || [];
}

export function getMeetLink(event: CalendarEvent): string | null {
  if (event.hangoutLink) {
    return event.hangoutLink;
  }

  // Try conferenceData entry points
  if (event.conferenceData?.entryPoints) {
    const videoEntry = event.conferenceData.entryPoints.find((ep) => ep.entryPointType === 'video');
    if (videoEntry) {
      return videoEntry.uri;
    }
  }

  // Check description and location for Zoom/Teams links
  const text = `${event.description || ''} ${event.location || ''}`;

  // Match Zoom links
  const zoomMatch = text.match(/https?:\/\/([a-z0-9\-]+\.)?zoom\.us\/[^\s\)\"<]*/i);
  if (zoomMatch) {
    return zoomMatch[0];
  }

  // Match Microsoft Teams links
  const teamsMatch = text.match(/https?:\/\/teams\.microsoft\.com\/[^\s\)\"<]*/i);
  if (teamsMatch) {
    return teamsMatch[0];
  }

  // Match Teams meeting links (alternative format)
  const teamsLinkMatch = text.match(/https?:\/\/teams\.live\.com\/[^\s\)\"<]*/i);
  if (teamsLinkMatch) {
    return teamsLinkMatch[0];
  }

  return null;
}

export function getMeetingType(event: CalendarEvent): 'google-meet' | 'zoom' | 'teams' | 'other' {
  const meetLink = getMeetLink(event);

  if (!meetLink) return 'other';

  if (meetLink.includes('meet.google.com') || event.hangoutLink) {
    return 'google-meet';
  }

  if (meetLink.includes('zoom.us')) {
    return 'zoom';
  }

  if (meetLink.includes('teams.microsoft.com') || meetLink.includes('teams.live.com')) {
    return 'teams';
  }

  return 'other';
}

export function isEventNow(event: CalendarEvent): boolean {
  const now = new Date();
  const start = new Date(event.start.dateTime);
  const end = new Date(event.end.dateTime);
  return now >= start && now <= end;
}

export function isEventUpcoming(event: CalendarEvent): boolean {
  const now = new Date();
  const start = new Date(event.start.dateTime);
  // Consider upcoming if starts within 15 minutes
  const fifteenMinutes = 15 * 60 * 1000;
  return start > now && start.getTime() - now.getTime() <= fifteenMinutes;
}
